import isEqual from 'lodash/isEqual'
import uniqWith from 'lodash/uniqWith'

import { useNCAARegionsFeature } from '@/optimizely/ncaa-regions-feature/hooks/use-ncaa-regions-feature'

export enum NCAARound {
    SWEET16 = 'Sweet 16',
    ELITE8 = 'Elite 8',
    SEMIFINALS = 'Semifinals',
    CHAMPIONSHIP = 'Championship',
    ALL_SESSIONS = 'All Sessions',
    FINAL_FOUR = 'Final Four',
}

export enum NCAARegion {
    WEST = 'West Regional',
    EAST = 'East Regional',
    SOUTH = 'South Regional',
    MIDWEST = 'Midwest Regional',
}

export type NCAAProperties = {
    isFinalFour: boolean
    title: string
    pillLabels: string[]
    matchups?: [string, string][][]
    isNCAARegionsFeatureEnabled: boolean
    customDates?: string
}

export const useNCAAProperties = (name: string, isDesktop?: boolean): NCAAProperties => {
    const [
        isNCAARegionsFeatureEnabled,
        {
            semifinals_teams: semifinalsTeams,
            championship_teams: championshipTeams,
            east_regional_dates: eastRegionalDates,
            south_regional_dates: southRegionalDates,
            midwest_regional_dates: midwestRegionalDates,
            west_regional_dates: westRegionalDates,
            semifinals_dates: semifinalsDates,
        },
    ] = useNCAARegionsFeature(isDesktop)

    const pillLabels: string[] = []
    const isFinalFour =
        name.includes(NCAARound.SEMIFINALS) ||
        name.includes(NCAARound.CHAMPIONSHIP) ||
        name.includes(NCAARound.FINAL_FOUR)

    Object.values(NCAARound).forEach((value) => {
        if (value !== NCAARound.FINAL_FOUR) {
            if (value === NCAARound.ALL_SESSIONS && name.includes(value)) {
                if (isFinalFour) {
                    pillLabels.push(NCAARound.SEMIFINALS, NCAARound.CHAMPIONSHIP)
                } else {
                    pillLabels.push(NCAARound.SWEET16, NCAARound.ELITE8)
                }
            } else if (name.includes(value)) {
                pillLabels.push(value)
            }
        }
    })

    const title = name.split('(')[0]
    let caption: string | undefined
    if (isFinalFour && semifinalsTeams && pillLabels.includes(NCAARound.SEMIFINALS)) {
        caption = semifinalsTeams
    } else if (isFinalFour && championshipTeams && pillLabels.includes(NCAARound.CHAMPIONSHIP)) {
        caption = championshipTeams
    }

    const matchups: [string, string][][] | undefined = caption?.split(',').map((matchup) =>
        matchup.split('vs.').map((team) => {
            const [rank, ...rest] = team.trim().split(' ')
            const teamName = rest.join(' ')
            return [rank, teamName]
        }),
    )

    let customDates: string | undefined
    if (eastRegionalDates && name.includes(`${NCAARegion.EAST} - ${NCAARound.ALL_SESSIONS}`)) {
        customDates = eastRegionalDates
    } else if (westRegionalDates && name.includes(`${NCAARegion.WEST} - ${NCAARound.ALL_SESSIONS}`)) {
        customDates = westRegionalDates
    } else if (southRegionalDates && name.includes(`${NCAARegion.SOUTH} - ${NCAARound.ALL_SESSIONS}`)) {
        customDates = southRegionalDates
    } else if (midwestRegionalDates && name.includes(`${NCAARegion.MIDWEST} - ${NCAARound.ALL_SESSIONS}`)) {
        customDates = midwestRegionalDates
    } else if (semifinalsDates && name.includes(`${NCAARound.FINAL_FOUR} - ${NCAARound.ALL_SESSIONS}`)) {
        customDates = semifinalsDates
    }

    return {
        pillLabels: uniqWith(pillLabels, isEqual),
        title,
        matchups,
        isFinalFour,
        isNCAARegionsFeatureEnabled,
        customDates,
    }
}
